<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="1600"
      scrollable
    >
      <v-card>
        <v-card-title>
          Employee Documents
          <v-spacer></v-spacer>
          <v-select
            dense
            hide-details
            label="Group by"
            item-text="text"
            item-value="value"
            multiple
            :items="groupByItems"
            clearable
            @change="setGroupHeaders"
            return-object
            @click:clear="groupByValue = null"
          ></v-select>
          <v-spacer></v-spacer>
          <v-text-field
            dense
            hide-details
            append-icon="mdi-magnify"
            v-model="search"
            label="Search Document"
          ></v-text-field>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            ref="tableDocuments"
            dense
            height="600px"
            fixed-header
            :custom-group="customGroupBy"
            :group-by="groupByValue"
            :loading="isLoading"
            :search="search"
            :headers="headers"
            :items="documents"
            :mobile-breakpoint="0"
            :hide-default-footer="showPagination"
            :disable-pagination="showPagination"
            :footer-props="{
              'items-per-page-options': [50, 100, -1]
            }"
            @update:group-by="closeGroups"
          >
            <template v-slot:[`group.header`]="{ items, isOpen, toggle, headers }">
              <td
                :colspan="headers.length - 1"
                class="primary white--text group-td"
                style="border-bottom: 2px solid white !important;"
              >
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    small
                    @click="toggle"
                  >
                    <v-icon color="white">
                      {{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </v-btn>
                  <span class="subtitle-2 ml-2">{{ groupTitle(items[0]) }}</span>
                </div>
              </td>
            </template>
            <template v-slot:[`item.Document_Of_Employee`]="{ item }">
              <v-select
                v-if="editMode && editingDocument.Document_ID === item.Document_ID"
                v-model="editingDocument.Document_Of_Employee_ID"
                dense
                hide-details
                :items="employeeList"
                item-text="full_name"
                item-value="employee_id"
                :disabled="isLoading"
                :rules="[rules.requitedText]"
              >
                <template v-slot:prepend-item>
                  <div class="px-2">
                    <v-text-field
                      label="Search employee"
                      v-model="searchEmployee"
                    ></v-text-field>
                  </div>
                </template>
              </v-select>
              <span v-else>{{ item.Document_Of_Employee }}</span>
            </template>
            <template v-slot:[`item.Document_Name`]="{ item }">
              <v-text-field
                v-if="editMode && editingDocument.Document_ID === item.Document_ID"
                :readonly="!editingDocument.File && fileWasChangedEditMode"
                v-model="editingDocument.Document_Name"
                dense
                persistent-hint
                :disabled="isLoading"
                :hint="editingDocument.File ? convertToKB(editingDocument.File.size) : 'No file selected'"
                :rules="[rules.requiredFile]"
              >
                <template v-slot:append>
                  <v-file-input
                    hide-input
                    hide-details
                    :disabled="isLoading"
                    dense
                    @change="onFileChangeEditMode"
                    v-model="editingDocument.File"
                    accept="application/pdf, image/*"
                  >
                  </v-file-input>
                </template>
              </v-text-field>
              <a @click="downloadDocument(item)" v-else>{{ item.Document_Name }}</a>
            </template>
            <template v-slot:[`item.Updated_By_Employee`]="{ item }">
              <span v-if="editMode && editingDocument.Document_ID === item.Document_ID">
                {{ user.full_name }}
              </span>
              <span v-else>{{ item.Updated_By_Employee }}</span>
            </template>
            <template v-slot:[`item.Type`]="{ item }">
              <v-select
                v-if="editMode && editingDocument.Document_ID === item.Document_ID"
                v-model="editingDocument.Document_Type_ID"
                :items="documentsTypes"
                item-text="Name"
                hide-details
                :disabled="isLoading"
                dense
                :rules="[rules.requitedText]"
                item-value="Type_ID"
              >
              </v-select>
              <span v-else>{{ item.Type }}</span>
            </template>
            <template v-slot:[`item.Updated_Date`]="{ item }">
              <span v-if="editMode && editingDocument.Document_ID === item.Document_ID"></span>
              <span v-else>{{ item.Updated_Date }}</span>
            </template>
            <template v-slot:[`item.Notes`]="{ item }">
              <v-textarea
                v-if="editMode && editingDocument.Document_ID === item.Document_ID"
                v-model="editingDocument.Notes"
                rows="1"
                :disabled="isLoading"
                auto-grow
                hide-details
                dense
              ></v-textarea>
              <span v-else>{{ item.Notes }}</span>
            </template>
            <template v-slot:[`item.Actions`]="{ item }">
              <v-btn
                :disabled="isLoading || addNewMode || (editMode && editingDocument.Document_ID !== item.Document_ID) || (editMode && !isEditedDocumentReady && editingDocument.Document_ID === item.Document_ID)"
                :loading="editMode && editingDocument.Document_ID === item.Document_ID && isLoading"
                :color="editMode && editingDocument.Document_ID === item.Document_ID ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item)"
              >
                <v-icon>
                  {{ editingDocument?.Document_ID === item.Document_ID ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="editingDocument?.Document_ID !== item.Document_ID"
                :disabled="editMode || addNewMode || isLoading"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
              <v-btn
                v-if="editMode && editingDocument.Document_ID === item.Document_ID"
                :disabled="editMode && isLoading"
                color="primary"
                small
                icon
                @click="cancelEditDocument"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
            <template v-slot:[`body.prepend`]>
              <tr v-if="addNewMode">
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                  <v-select
                    v-if="addNewMode"
                    v-model="newDocument.Document_Of_Employee_ID"
                    dense
                    hide-details
                    :items="employeeList"
                    item-text="full_name"
                    item-value="employee_id"
                    :disabled="isLoading"
                    :rules="[rules.requitedText]"
                  >
                    <template v-slot:prepend-item>
                      <div class="px-2">
                        <v-text-field
                          label="Search employee"
                          v-model="searchEmployee"
                        ></v-text-field>
                      </div>
                    </template>
                  </v-select>
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                  <v-text-field
                    v-if="addNewMode"
                    :readonly="!newDocument.File"
                    v-model="newDocument.Document_Name"
                    dense
                    persistent-hint
                    :disabled="isLoading"
                    :hint="newDocument.File ? convertToKB(newDocument.File.size) : 'No file selected'"
                    :rules="[rules.requiredFile]"
                  >
                    <template v-slot:append>
                      <v-file-input
                        hide-input
                        hide-details
                        dense
                        @change="onFileChangeAddMode"
                        v-model="newDocument.File"
                        :disabled="isLoading"
                        :rules="[rules.requitedText]"
                        accept="application/pdf, image/*"
                      >
                      </v-file-input>
                    </template>
                  </v-text-field>
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                  <span v-if="addNewMode">{{ user.full_name }}</span>
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                 }"
                >
                  <v-select
                    v-if="addNewMode"
                    v-model="newDocument.Document_Type_ID"
                    :items="documentsTypes"
                    item-text="Name"
                    hide-details
                    dense
                    :disabled="isLoading"
                    :rules="[rules.requitedText]"
                    item-value="Type_ID"
                  >
                  </v-select>
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                </td>
                <td
                  class="px-2 text-no-wrap"
                  :class="{
                  'v-data-table__divider': addNewMode
                }"
                >
                  <v-textarea
                    v-if="addNewMode"
                    v-model="newDocument.Notes"
                    rows="1"
                    auto-grow
                    hide-details
                    dense
                    :disabled="isLoading"
                  ></v-textarea>
                </td>
                <td class="text-center text-no-wrap">
                  <v-btn
                    v-if="addNewMode"
                    :disabled="!isNewDocumentReady || isLoading"
                    :loading="isLoading"
                    icon
                    color="success"
                    small
                    @click.prevent="addNewDocument"
                  >
                    <v-icon>mdi-check-circle</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="addNewMode"
                    :disabled="isLoading"
                    icon
                    color="primary"
                    small
                    @click.prevent="cancelAddNewDocument"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-4">
          <v-spacer></v-spacer>
          <v-btn
            :disabled="addNewMode || isLoading || editMode"
            color="success"
            small
            @click.prevent="addNewDocument"
          >
            Add New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="deleteDocumentDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Are you sure?
        </v-card-title>
        <v-card-text>
          <v-textarea
            spellcheck="false"
            v-model="deleteNotes"
            label="Deleted notes"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" small outlined @click="cancelDeleteDocument">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn :disabled="!deleteNotes" color="red darken-2 white--text" small @click="deleteDocument">Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
export default {
  name: 'EmployeeDocuments',
  components: {},
  props: {
    value: {
      required: false,
      default: false
    },
    user: {
      required: false
    }
  },
  data () {
    return {
      addNewMode: false,
      documents: [],
      documentsTypes: [],
      deleteDocumentDialog: false,
      deleteNotes: '',
      documentIDToDelete: null,
      documentOldName: null,
      documentIDToEdit: null,
      downloadLink: document.createElement('a'),
      groupByValue: null,
      groupByText: null,
      search: '',
      searchEmployee: '',
      editMode: false,
      editingDocument: null,
      isLoading: false,
      fileWasChangedEditMode: false,
      newDocument: null,
      rules: {
        requiredFile: v => (v && !!v.toString().trim()) || 'The file name is required',
        requitedText: v => !!v || 'Required'
      },
      groupByItems: [
        {
          text: 'Document Owner',
          value: 'Document_Of_Employee_Group'
        },
        {
          text: 'Uploaded by',
          value: 'Uploaded_By_Employee_Group'
        },
        {
          text: 'Updated by',
          value: 'Updated_By_Employee_Group'
        },
        {
          text: 'Type',
          value: 'Type_Group'
        },
        {
          text: 'Uploaded Date',
          value: 'Uploaded_Date_Group'
        },
        {
          text: 'Updated Date',
          value: 'Updated_Date_Group'
        }
      ],
      headers: [
        // v-data-table group headers
        {
          text: 'Document Owner Group',
          value: 'groupValues.Document_Of_Employee_Group',
          class: 'd-none',
          cellClass: 'd-none'
        },
        {
          text: 'Uploaded by Group',
          value: 'groupValues.Uploaded_By_Employee_Group',
          class: 'd-none',
          cellClass: 'd-none'
        },
        {
          text: 'Updated by Group',
          value: 'groupValues.Updated_By_Employee_Group',
          class: 'd-none',
          cellClass: 'd-none'
        },
        {
          text: 'Type Group',
          value: 'groupValues.Type_Group',
          class: 'd-none',
          cellClass: 'd-none'
        },
        {
          text: 'Uploaded Date Group',
          value: 'groupValues.Uploaded_Date_Group',
          class: 'd-none',
          cellClass: 'd-none'
        },
        {
          text: 'Updated Date Group',
          value: 'groupValues.Updated_Date_Group',
          class: 'd-none',
          cellClass: 'd-none'
        },
        // v-data-table headers
        {
          text: 'Document Owner',
          value: 'Document_Of_Employee',
          divider: true,
          width: '12%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Document Title',
          value: 'Document_Name',
          divider: true,
          width: '15%',
          cellClass: 'px-2',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Uploaded by',
          value: 'Uploaded_By_Employee',
          divider: true,
          width: '10%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Updated by',
          value: 'Updated_By_Employee',
          divider: true,
          width: '10%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Type',
          value: 'Type',
          divider: true,
          width: '10%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Uploaded Date',
          value: 'Uploaded_Date',
          divider: true,
          width: '7%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Updated Date',
          value: 'Updated_Date',
          divider: true,
          width: '7%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Notes',
          value: 'Notes',
          divider: true,
          cellClass: 'px-2',
          class: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          width: 100,
          sortable: false,
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          value: 'Actions'
        }
      ]
    }
  },
  mounted () {
    this.getEmployeeDocuments()
    this.getEmployeeDocumentsTypes()
    this.getEmployeeList()
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    employeeList () {
      const r = new RegExp(this.searchEmployee, 'gi')
      return this.$store.getters.getEmployee.filter(item => {
        r.lastIndex = 0
        return r.test(item.full_name)
      })
    },
    isNewDocumentReady () {
      return this.addNewMode && this.newDocument && this.newDocument.Document_Name.trim().length &&
        this.newDocument.Document_Of_Employee_ID && this.newDocument.Uploaded_By_Employee_ID &&
        this.newDocument.File && this.newDocument.Document_Type_ID
    },
    isEditedDocumentReady () {
      return this.editingDocument && this.editingDocument.Document_Name.trim().length &&
        this.editingDocument.Document_Type_ID
    },
    showPagination () {
      return !!this.groupByValue && !!this.groupByValue.length
    }
  },
  methods: {
    closeGroups () {
      setTimeout(() => {
        const table = this.$refs.tableDocuments
        const keys = Object.keys(table.$vnode.componentInstance.openCache)
        keys.forEach(x => {
          table.$vnode.componentInstance.openCache[x] = false
        })
      })
    },
    setGroupHeaders (items) {
      this.groupByValue = items.map(el => el.value)
      this.groupByText = items.map(el => el.text)
    },
    customGroupBy (items, groupBy, groupDesc) {
      const groups = []
      items.forEach(item => {
        let cId = ''
        for (let i = 0; i < groupBy.length; i++) {
          cId += item.groupValues[groupBy[i]]
        }
        const group = groups.find(g => g.name === cId)
        if (group) {
          group.items.push(item)
        } else {
          groups.push({
            name: cId,
            items: [item]
          })
        }
      })
      return groups
    },
    groupTitle (item) {
      let str = ''
      for (let i = 0; i < this.groupByValue.length; i++) {
        str += this.groupByText[i] + ': ' + (item.groupValues[this.groupByValue[i]] ? item.groupValues[this.groupByValue[i]] : 'N/A') + (i === this.groupByValue.length - 1 ? '' : ', ')
      }
      return str
    },
    convertToKB (bites) {
      return (bites / 1024).toFixed(2) + ' KB'
    },
    allowTableSorting (boolean) {
      for (const header of this.headers) {
        if (header.value !== 'Actions') {
          header.sortable = boolean
        }
      }
    },
    parseFileNameAndExtension (fullFileName) {
      const name = fullFileName.slice(0, fullFileName.lastIndexOf('.')).trim()
      const extension = fullFileName.split('.').pop()
      return {
        name,
        extension
      }
    },
    getEmployeeList () {
      if (!this.employeeList.length) {
        this.axios.get('/api/employeelist', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
          this.$store.dispatch('setEmployeeList', { employeeList: response.data })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    getEmployeeDocuments () {
      this.isLoading = true
      this.axios.get('/api/document/list', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const documents = res.data
        for (const document of documents) {
          document.groupValues = {
            Document_Of_Employee_Group: document.Document_Of_Employee,
            Type_Group: document.Type,
            Updated_By_Employee_Group: document.Updated_By_Employee,
            Updated_Date_Group: document.Updated_Date,
            Uploaded_By_Employee_Group: document.Uploaded_By_Employee,
            Uploaded_Date_Group: document.Uploaded_Date
          }
        }

        this.documents = documents
        this.isLoading = false
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getEmployeeDocumentsTypes () {
      this.axios.get('/api/document_type', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.documentsTypes = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    downloadDocument (item) {
      this.isLoading = true
      this.axios.get(`/api/document/${item.S3_Name}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const contentType = res.data.ContentType
        const buffer = res.data.Body.data
        this.openFile(item.Document_Name, buffer, contentType)
        this.isLoading = false
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    openFile (filename, data, type) {
      this.downloadLink.href = this.linkToFile(data, type)
      this.downloadLink.download = filename
      this.downloadLink.click()
    },
    linkToFile (data, type) {
      let res = ''
      let binary = ''
      const bytes = new Uint8Array(data)
      const len = bytes.byteLength
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      res = `data:${type};base64, ${window.btoa(binary)}`
      return res
    },
    addNewDocument () {
      if (!this.addNewMode) {
        this.newDocument = {
          Document_Name: '',
          Document_Of_Employee_ID: null,
          Uploaded_By_Employee_ID: this.user.employee_id,
          File: null,
          Extension: '',
          Document_Type_ID: null,
          Uploaded_Date: '',
          Notes: ''
        }
        this.addNewMode = true
        setTimeout(() => {
          const table = this.$refs.tableDocuments
          const wrapper = table.$el.querySelector('div.v-data-table__wrapper')
          this.$vuetify.goTo(table, { container: wrapper })
        })
      } else {
        this.uploadDocument()
      }
    },
    onFileChangeAddMode () {
      if (this.newDocument.File) {
        const {
          name,
          extension
        } = this.parseFileNameAndExtension(this.newDocument.File.name)
        this.newDocument.Document_Name = name
        this.newDocument.Extension = extension
      } else {
        this.newDocument.Document_Name = ''
        this.newDocument.Extension = ''
        this.newDocument.File = null
      }
    },
    cancelAddNewDocument () {
      this.newDocument = null
      this.addNewMode = false
      this.searchEmployee = ''
    },
    uploadDocument () {
      this.isLoading = true
      this.newDocument.Uploaded_Date = new Date().toISOString().substring(0, 10)
      const formData = new FormData()
      this.newDocument.Document_Name = this.newDocument.Document_Name.trim()
      Object.entries(this.newDocument).forEach(([key, value]) => {
        formData.append(key, value)
      })
      this.axios.post('/api/document', formData, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getEmployeeDocuments()
        this.cancelAddNewDocument()
        this.isLoading = false
        this.$store.dispatch('setSuccess', { successMsg: 'The document successfully uploaded' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    deleteDocument () {
      this.isLoading = true
      const body = {
        Deleted_Notes: this.deleteNotes
      }
      this.axios.put(`/api/document_soft_delete/${this.documentIDToDelete}`, body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.cancelDeleteDocument()
        this.getEmployeeDocuments()
        this.isLoading = false
        this.$store.dispatch('setSuccess', { successMsg: 'The document successfully deleted' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    onDeleteClick (item) {
      const date = new Date().toLocaleString()
      this.deleteDocumentDialog = true
      this.documentIDToDelete = item.Document_ID
      this.deleteNotes = `Deleted by: ${this.user.login}, Date: ${date}`
    },
    cancelDeleteDocument () {
      this.deleteNotes = ''
      this.documentIDToDelete = null
      this.deleteDocumentDialog = false
    },
    onEditClick (item) {
      if (!this.editMode) {
        this.allowTableSorting(false)
        this.editMode = true
        this.documentOldName = item.Document_Name
        this.documentIDToEdit = item.Document_ID
        const documentTypeID = this.documentsTypes.find(el => el.Name === item.Type)

        this.editingDocument = {
          Document_ID: item.Document_ID,
          Document_Name: item.Document_Name,
          S3_Name: item.S3_Name,
          Location: item.Location,
          Updated_By_Employee_ID: this.user.employee_id,
          Uploaded_By_Employee_ID: item.Uploaded_By_Employee_ID,
          Document_Of_Employee_ID: item.Document_Of_Employee_ID,
          Document_Type_ID: documentTypeID.Type_ID,
          Updated_Date: '',
          Uploaded_Date: item.Uploaded_Date,
          Notes: item.Notes,
          File: null,
          Extension: ''
        }
      } else {
        this.putEmployeeDocument()
      }
    },
    onFileChangeEditMode () {
      if (this.editingDocument.File) {
        const {
          name,
          extension
        } = this.parseFileNameAndExtension(this.editingDocument.File.name)
        this.editingDocument.Document_Name = name
        this.editingDocument.Extension = extension
        this.fileWasChangedEditMode = true
      } else {
        this.fileWasChangedEditMode = false
        this.editingDocument.File = null
        this.editingDocument.Document_Name = this.documentOldName
      }
    },
    cancelEditDocument () {
      this.allowTableSorting(true)
      this.editMode = false
      this.editingDocument = null
      this.fileWasChangedEditMode = false
      this.documentOldName = null
      this.documentIDToEdit = null
    },
    putEmployeeDocument () {
      this.isLoading = true
      this.editingDocument.Updated_Date = new Date().toISOString().substring(0, 10)
      this.editingDocument.Document_Name = this.editingDocument.Document_Name.trim()
      if (this.editingDocument.File) {
        this.editingDocument.Uploaded_Date = new Date().toISOString().substring(0, 10)
        this.editingDocument.Uploaded_By_Employee_ID = this.user.employee_id
      }
      const formData = new FormData()
      Object.entries(this.editingDocument).forEach(([key, value]) => {
        formData.append(key, value)
      })
      this.axios.put(`/api/document/${this.documentIDToEdit}`, formData, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.cancelEditDocument()
        this.getEmployeeDocuments()
        this.isLoading = false
        this.$store.dispatch('setSuccess', { successMsg: 'The document successfully updated' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep {
  tr:has(.group-td) {
    height: auto !important;
    border-bottom: 2px solid white;
  }
}
</style>
